import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AnalyticsFilterState {
  period: number
  granularity: 'day' | 'week' | 'month' | 'quarter'
  device: string
  country?: string
  region?: string
  city?: string
  startDate?: string
  endDate?: string
  compareStartDate?: string
  compareEndDate?: string
}

interface AnalyticsFiltersProps {
  filters: AnalyticsFilterState
  onChange: (filters: AnalyticsFilterState) => void
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function fmt(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function parseDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function isInRange(day: Date, start: Date | null, end: Date | null): boolean {
  if (!start || !end) return false
  const t = day.getTime()
  return t >= start.getTime() && t <= end.getTime()
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

function firstDayOfWeek(year: number, month: number): number {
  const d = new Date(year, month, 1).getDay()
  // Convert Sunday=0 to Monday-based (Mon=0 ... Sun=6)
  return d === 0 ? 6 : d - 1
}

const MONTH_NAMES = [
  'Janvier', 'F\u00e9vrier', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Ao\u00fbt', 'Septembre', 'Octobre', 'Novembre', 'D\u00e9cembre',
]

const SHORT_MONTH_NAMES = [
  'janv.', 'f\u00e9vr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'ao\u00fbt', 'sept.', 'oct.', 'nov.', 'd\u00e9c.',
]

const DAY_HEADERS = ['L', 'M', 'M', 'J', 'V', 'S', 'D']

function formatDisplayDate(startDate: string | undefined, endDate: string | undefined): string {
  if (!startDate || !endDate) return 'S\u00e9lectionner une p\u00e9riode'
  const s = parseDate(startDate)
  const e = parseDate(endDate)
  const sDay = s.getDate()
  const eDay = e.getDate()
  const sMonth = SHORT_MONTH_NAMES[s.getMonth()]
  const eMonth = SHORT_MONTH_NAMES[e.getMonth()]
  const sYear = s.getFullYear()
  const eYear = e.getFullYear()

  if (sYear === eYear) {
    if (s.getMonth() === e.getMonth()) {
      return `${sDay} \u2014 ${eDay} ${eMonth} ${eYear}`
    }
    return `${sDay} ${sMonth} \u2014 ${eDay} ${eMonth} ${eYear}`
  }
  return `${sDay} ${sMonth} ${sYear} \u2014 ${eDay} ${eMonth} ${eYear}`
}

// ---------------------------------------------------------------------------
// Preset definitions
// ---------------------------------------------------------------------------

type PresetKey = 'custom' | 'today' | 'yesterday' | '7d' | '28d' | '30d' | 'this_month' | 'last_month' | '90d' | 'last_quarter' | 'this_year' | 'last_year'

interface Preset {
  key: PresetKey
  label: string
}

const presets: Preset[] = [
  { key: 'custom', label: 'Personnalis\u00e9e' },
  { key: 'today', label: "Aujourd'hui" },
  { key: 'yesterday', label: 'Hier' },
  { key: '7d', label: 'Les 7 derniers jours' },
  { key: '28d', label: 'Les 28 derniers jours' },
  { key: '30d', label: 'Les 30 derniers jours' },
  { key: 'this_month', label: 'Ce mois-ci' },
  { key: 'last_month', label: 'Le mois dernier' },
  { key: '90d', label: 'Les 90 derniers jours' },
  { key: 'last_quarter', label: 'Le dernier trimestre' },
  { key: 'this_year', label: 'Cette ann\u00e9e (depuis janvier)' },
  { key: 'last_year', label: 'Derni\u00e8re ann\u00e9e civile' },
]

function computePresetDates(key: PresetKey): { startDate: string; endDate: string } {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  switch (key) {
    case 'today':
      return { startDate: fmt(today), endDate: fmt(today) }
    case 'yesterday':
      return { startDate: fmt(yesterday), endDate: fmt(yesterday) }
    case '7d': {
      const s = new Date(yesterday)
      s.setDate(yesterday.getDate() - 6)
      return { startDate: fmt(s), endDate: fmt(yesterday) }
    }
    case '28d': {
      const s = new Date(yesterday)
      s.setDate(yesterday.getDate() - 27)
      return { startDate: fmt(s), endDate: fmt(yesterday) }
    }
    case '30d': {
      const s = new Date(yesterday)
      s.setDate(yesterday.getDate() - 29)
      return { startDate: fmt(s), endDate: fmt(yesterday) }
    }
    case 'this_month':
      return { startDate: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), endDate: fmt(today) }
    case 'last_month': {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const e = new Date(today.getFullYear(), today.getMonth(), 0)
      return { startDate: fmt(s), endDate: fmt(e) }
    }
    case '90d': {
      const s = new Date(yesterday)
      s.setDate(yesterday.getDate() - 89)
      return { startDate: fmt(s), endDate: fmt(yesterday) }
    }
    case 'last_quarter': {
      const currentQuarter = Math.floor(today.getMonth() / 3)
      let qYear = today.getFullYear()
      let qMonth = (currentQuarter - 1) * 3
      if (currentQuarter === 0) {
        qYear -= 1
        qMonth = 9
      }
      const s = new Date(qYear, qMonth, 1)
      const e = new Date(qYear, qMonth + 3, 0)
      return { startDate: fmt(s), endDate: fmt(e) }
    }
    case 'this_year':
      return { startDate: fmt(new Date(today.getFullYear(), 0, 1)), endDate: fmt(today) }
    case 'last_year': {
      const y = today.getFullYear() - 1
      return { startDate: fmt(new Date(y, 0, 1)), endDate: fmt(new Date(y, 11, 31)) }
    }
    default:
      return { startDate: fmt(new Date(yesterday.getTime() - 29 * 86400000)), endDate: fmt(yesterday) }
  }
}

function computeComparisonDates(
  mode: 'previous_period' | 'previous_year' | 'custom',
  startDate: string,
  endDate: string,
): { compareStartDate: string; compareEndDate: string } {
  const start = parseDate(startDate)
  const end = parseDate(endDate)

  if (mode === 'previous_year') {
    const cs = new Date(start)
    cs.setFullYear(cs.getFullYear() - 1)
    const ce = new Date(end)
    ce.setFullYear(ce.getFullYear() - 1)
    return { compareStartDate: fmt(cs), compareEndDate: fmt(ce) }
  }

  // previous_period
  const durationMs = end.getTime() - start.getTime()
  const days = Math.round(durationMs / 86400000)
  const ce = new Date(start)
  ce.setDate(ce.getDate() - 1)
  const cs = new Date(ce)
  cs.setDate(cs.getDate() - days)
  return { compareStartDate: fmt(cs), compareEndDate: fmt(ce) }
}

function detectPresetKey(startDate?: string, endDate?: string): PresetKey {
  if (!startDate || !endDate) return '30d'
  for (const preset of presets) {
    if (preset.key === 'custom') continue
    const { startDate: ps, endDate: pe } = computePresetDates(preset.key)
    if (ps === startDate && pe === endDate) return preset.key
  }
  return 'custom'
}

// ---------------------------------------------------------------------------
// Calendar Month component
// ---------------------------------------------------------------------------

function CalendarMonth({
  year,
  month,
  rangeStart,
  rangeEnd,
  hoverDay,
  onDayClick,
  onDayHover,
}: {
  year: number
  month: number
  rangeStart: Date | null
  rangeEnd: Date | null
  hoverDay: Date | null
  onDayClick: (d: Date) => void
  onDayHover: (d: Date | null) => void
}) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const numDays = daysInMonth(year, month)
  const startOffset = firstDayOfWeek(year, month)

  // Compute effective end for hover preview
  const effectiveEnd = rangeStart && !rangeEnd && hoverDay ? hoverDay : rangeEnd

  const cells: React.ReactNode[] = []

  // Empty cells before first day
  for (let i = 0; i < startOffset; i++) {
    cells.push(<div key={`empty-${i}`} className="w-8 h-8" />)
  }

  for (let d = 1; d <= numDays; d++) {
    const date = new Date(year, month, d)
    const isToday = isSameDay(date, today)
    const isStart = rangeStart ? isSameDay(date, rangeStart) : false
    const isEnd = effectiveEnd ? isSameDay(date, effectiveEnd) : false
    const inRange = isInRange(date, rangeStart, effectiveEnd)
    const isEndpoint = isStart || isEnd

    let className = 'w-8 h-8 text-sm flex items-center justify-center cursor-pointer transition-colors relative'

    if (isEndpoint) {
      className += ' bg-[#6366F1] text-white rounded-full z-10'
    } else if (inRange) {
      className += ' bg-[#6366F1]/10 text-[#1B2559]'
    } else {
      className += ' text-[#1B2559] hover:bg-[#F4F5FA] rounded-full'
    }

    if (isToday && !isEndpoint) {
      className += ' ring-1 ring-[#6366F1]'
      if (!inRange) className += ' rounded-full'
    }

    cells.push(
      <div
        key={d}
        className={className}
        onClick={() => onDayClick(date)}
        onMouseEnter={() => onDayHover(date)}
        onMouseLeave={() => onDayHover(null)}
      >
        {d}
      </div>,
    )
  }

  return (
    <div className="flex-shrink-0 w-[220px]">
      <div className="text-sm font-semibold text-[#1B2559] uppercase mb-2">
        {MONTH_NAMES[month]} {year}
      </div>
      <div className="grid grid-cols-7 gap-0 mb-1">
        {DAY_HEADERS.map((h, i) => (
          <div key={i} className="w-8 h-6 text-xs text-[#8B8FA7] flex items-center justify-center">
            {h}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0">{cells}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pill sub-component (for granularity / device outside the panel)
// ---------------------------------------------------------------------------

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition-all cursor-pointer whitespace-nowrap
        ${active ? 'bg-white text-[#1B2559] shadow-sm' : 'text-[#8B8FA7] hover:text-[#555A73]'}
      `}
    >
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Granularity & Device options
// ---------------------------------------------------------------------------

const granularityOptions = [
  { label: 'Jour', value: 'day' as const },
  { label: 'Semaine', value: 'week' as const },
  { label: 'Mois', value: 'month' as const },
  { label: 'Trimestre', value: 'quarter' as const },
]

const deviceOptions = [
  { label: 'Tous', value: 'all' },
  { label: 'Desktop', value: 'desktop' },
  { label: 'Mobile', value: 'mobile' },
]

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AnalyticsFilters({ filters, onChange }: AnalyticsFiltersProps) {
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Pending state (not applied until "Appliquer")
  const [pendingStart, setPendingStart] = useState<string>(filters.startDate ?? '')
  const [pendingEnd, setPendingEnd] = useState<string>(filters.endDate ?? '')
  const [pendingPreset, setPendingPreset] = useState<PresetKey>(() => detectPresetKey(filters.startDate, filters.endDate))
  const [compareEnabled, setCompareEnabled] = useState<boolean>(!!(filters.compareStartDate && filters.compareEndDate))
  const [compareMode, setCompareMode] = useState<'previous_period' | 'previous_year' | 'custom'>('previous_period')
  const [pendingCompareStart, setPendingCompareStart] = useState<string>(filters.compareStartDate ?? '')
  const [pendingCompareEnd, setPendingCompareEnd] = useState<string>(filters.compareEndDate ?? '')

  // Calendar selection state
  const [selectionStart, setSelectionStart] = useState<Date | null>(pendingStart ? parseDate(pendingStart) : null)
  const [selectionEnd, setSelectionEnd] = useState<Date | null>(pendingEnd ? parseDate(pendingEnd) : null)
  const [hoverDay, setHoverDay] = useState<Date | null>(null)
  const [picking, setPicking] = useState<'idle' | 'start'>('idle')

  // Calendar scroll: base month (the rightmost/current month). We show 3 months: baseMonth-2, baseMonth-1, baseMonth
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d }, [])
  const [calYear, setCalYear] = useState(today.getFullYear())
  const [calMonth, setCalMonth] = useState(today.getMonth())

  // Sync pending state when panel opens
  useEffect(() => {
    if (open) {
      const start = filters.startDate ?? ''
      const end = filters.endDate ?? ''
      setPendingStart(start)
      setPendingEnd(end)
      setPendingPreset(detectPresetKey(filters.startDate, filters.endDate))
      setCompareEnabled(!!(filters.compareStartDate && filters.compareEndDate))
      setPendingCompareStart(filters.compareStartDate ?? '')
      setPendingCompareEnd(filters.compareEndDate ?? '')
      setSelectionStart(start ? parseDate(start) : null)
      setSelectionEnd(end ? parseDate(end) : null)
      setPicking('idle')
      setHoverDay(null)

      // Set calendar to show the end date's month
      if (end) {
        const d = parseDate(end)
        setCalYear(d.getFullYear())
        setCalMonth(d.getMonth())
      } else {
        setCalYear(today.getFullYear())
        setCalMonth(today.getMonth())
      }
    }
  }, [open, filters.startDate, filters.endDate, filters.compareStartDate, filters.compareEndDate, today])

  // Close on click outside
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Recompute comparison dates when main dates or mode changes
  const recomputeComparison = useCallback((start: string, end: string, mode: 'previous_period' | 'previous_year' | 'custom', enabled: boolean) => {
    if (!enabled || mode === 'custom' || !start || !end) return
    const comp = computeComparisonDates(mode, start, end)
    setPendingCompareStart(comp.compareStartDate)
    setPendingCompareEnd(comp.compareEndDate)
  }, [])

  // Calendar months to display (3 months)
  const calendarMonths = useMemo(() => {
    const months: { year: number; month: number }[] = []
    for (let i = 2; i >= 0; i--) {
      let m = calMonth - i
      let y = calYear
      while (m < 0) { m += 12; y -= 1 }
      months.push({ year: y, month: m })
    }
    return months
  }, [calYear, calMonth])

  const navigateMonths = (direction: -1 | 1) => {
    let m = calMonth + direction
    let y = calYear
    if (m > 11) { m = 0; y += 1 }
    if (m < 0) { m = 11; y -= 1 }
    setCalMonth(m)
    setCalYear(y)
  }

  // Handle day click in calendar
  const handleDayClick = (date: Date) => {
    if (picking === 'idle' || picking === 'start') {
      // First click: set start
      setSelectionStart(date)
      setSelectionEnd(null)
      setPicking('start')
      setPendingStart(fmt(date))
      setPendingEnd('')
      setPendingPreset('custom')
    }
    if (picking === 'start' && selectionStart) {
      // If clicking before selectionStart, swap
      let s = selectionStart
      let e = date
      if (date.getTime() < selectionStart.getTime()) {
        s = date
        e = selectionStart
      }
      // Second click: if same as start, treat as single day; otherwise set range
      if (isSameDay(s, e) && !selectionEnd) {
        // Wait for second click
        return
      }
      setSelectionStart(s)
      setSelectionEnd(e)
      setPendingStart(fmt(s))
      setPendingEnd(fmt(e))
      setPicking('idle')
      setPendingPreset(detectPresetKey(fmt(s), fmt(e)))
      recomputeComparison(fmt(s), fmt(e), compareMode, compareEnabled)
    }
  }

  // Actually we need a simpler two-click model
  const handleDayClickSimple = useCallback((date: Date) => {
    if (picking === 'idle') {
      // First click
      setSelectionStart(date)
      setSelectionEnd(null)
      setPendingStart(fmt(date))
      setPendingEnd('')
      setPicking('start')
      setPendingPreset('custom')
    } else {
      // Second click — determine order
      let s = selectionStart!
      let e = date
      if (date.getTime() < s.getTime()) {
        e = s
        s = date
      }
      setSelectionStart(s)
      setSelectionEnd(e)
      setPendingStart(fmt(s))
      setPendingEnd(fmt(e))
      setPicking('idle')
      setPendingPreset(detectPresetKey(fmt(s), fmt(e)))
      recomputeComparison(fmt(s), fmt(e), compareMode, compareEnabled)
    }
  }, [picking, selectionStart, compareMode, compareEnabled, recomputeComparison])

  // Handle preset click
  const handlePresetClick = (key: PresetKey) => {
    setPendingPreset(key)
    if (key === 'custom') return
    const { startDate, endDate } = computePresetDates(key)
    setPendingStart(startDate)
    setPendingEnd(endDate)
    setSelectionStart(parseDate(startDate))
    setSelectionEnd(parseDate(endDate))
    setPicking('idle')
    recomputeComparison(startDate, endDate, compareMode, compareEnabled)

    // Scroll calendar to show end date
    const d = parseDate(endDate)
    setCalYear(d.getFullYear())
    setCalMonth(d.getMonth())
  }

  // Handle apply
  const handleApply = () => {
    if (!pendingStart || !pendingEnd) return
    const patch: Partial<AnalyticsFilterState> = {
      startDate: pendingStart,
      endDate: pendingEnd,
    }
    if (compareEnabled) {
      patch.compareStartDate = pendingCompareStart
      patch.compareEndDate = pendingCompareEnd
    } else {
      patch.compareStartDate = undefined
      patch.compareEndDate = undefined
    }
    onChange({ ...filters, ...patch })
    setOpen(false)
  }

  // Handle cancel
  const handleCancel = () => {
    setOpen(false)
  }

  // Handle compare toggle
  const handleCompareToggle = () => {
    const next = !compareEnabled
    setCompareEnabled(next)
    if (next && pendingStart && pendingEnd && compareMode !== 'custom') {
      const comp = computeComparisonDates(compareMode, pendingStart, pendingEnd)
      setPendingCompareStart(comp.compareStartDate)
      setPendingCompareEnd(comp.compareEndDate)
    }
    if (!next) {
      setPendingCompareStart('')
      setPendingCompareEnd('')
    }
  }

  // Handle compare mode change
  const handleCompareModeChange = (mode: 'previous_period' | 'previous_year' | 'custom') => {
    setCompareMode(mode)
    if (mode !== 'custom' && pendingStart && pendingEnd) {
      const comp = computeComparisonDates(mode, pendingStart, pendingEnd)
      setPendingCompareStart(comp.compareStartDate)
      setPendingCompareEnd(comp.compareEndDate)
    }
  }

  // Direct update for granularity/device (outside panel)
  const update = useCallback(
    (patch: Partial<AnalyticsFilterState>) => {
      onChange({ ...filters, ...patch })
    },
    [filters, onChange],
  )

  // Display text for trigger
  const displayText = formatDisplayDate(filters.startDate, filters.endDate)

  return (
    <div className="flex flex-col gap-3">
      {/* Row: Trigger + Granularity + Device */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Date picker trigger */}
        <div className="relative">
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen(!open)}
            className="inline-flex items-center gap-2 bg-white border border-[#E2E4ED] rounded-lg px-3 py-1.5 text-sm text-[#1B2559] hover:border-[#C5C8D8] transition-colors cursor-pointer"
          >
            <CalendarDays className="w-4 h-4 text-[#8B8FA7]" />
            <span>{displayText}</span>
            <ChevronDown className="w-4 h-4 text-[#8B8FA7]" />
          </button>

          {/* Panel */}
          {open && (
            <div
              ref={panelRef}
              className="absolute top-full left-0 mt-1 bg-white border border-[#E2E4ED] rounded-xl shadow-lg z-50 min-w-[720px]"
            >
              <div className="flex">
                {/* Left column: Presets */}
                <div className="w-[220px] border-r border-[#E2E4ED] py-2 flex-shrink-0">
                  {presets.map((preset) => (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => handlePresetClick(preset.key)}
                      className={`
                        w-full text-left px-4 py-2 text-sm cursor-pointer transition-colors
                        ${pendingPreset === preset.key
                          ? 'bg-[#6366F1]/10 text-[#6366F1] font-medium'
                          : 'text-[#1B2559] hover:bg-[#F4F5FA]'
                        }
                      `}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                {/* Right column: Inputs + Calendar */}
                <div className="flex-1 flex flex-col">
                  {/* Date inputs */}
                  <div className="flex items-end gap-3 px-4 pt-4 pb-3">
                    <div>
                      <label className="block text-[10px] text-[#8B8FA7] uppercase mb-1">Date de d&eacute;but</label>
                      <input
                        type="text"
                        value={pendingStart}
                        onChange={(e) => {
                          setPendingStart(e.target.value)
                          setPendingPreset('custom')
                          if (e.target.value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                            setSelectionStart(parseDate(e.target.value))
                          }
                        }}
                        placeholder="AAAA-MM-JJ"
                        className="border border-[#E2E4ED] rounded px-2 py-1 text-sm w-[130px] text-[#1B2559] outline-none focus:border-[#6366F1]"
                      />
                    </div>
                    <span className="text-[#8B8FA7] text-sm pb-1">&mdash;</span>
                    <div>
                      <label className="block text-[10px] text-[#8B8FA7] uppercase mb-1">Date de fin</label>
                      <input
                        type="text"
                        value={pendingEnd}
                        onChange={(e) => {
                          setPendingEnd(e.target.value)
                          setPendingPreset('custom')
                          if (e.target.value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                            setSelectionEnd(parseDate(e.target.value))
                          }
                        }}
                        placeholder="AAAA-MM-JJ"
                        className="border border-[#E2E4ED] rounded px-2 py-1 text-sm w-[130px] text-[#1B2559] outline-none focus:border-[#6366F1]"
                      />
                    </div>
                  </div>

                  {/* Calendar navigation */}
                  <div className="flex items-center justify-between px-4 pb-2">
                    <button
                      type="button"
                      onClick={() => navigateMonths(-1)}
                      className="p-1 hover:bg-[#F4F5FA] rounded-full cursor-pointer transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4 text-[#8B8FA7]" />
                    </button>
                    <button
                      type="button"
                      onClick={() => navigateMonths(1)}
                      className="p-1 hover:bg-[#F4F5FA] rounded-full cursor-pointer transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 text-[#8B8FA7]" />
                    </button>
                  </div>

                  {/* Calendar grid: 3 months */}
                  <div className="flex gap-4 px-4 pb-4 overflow-x-auto">
                    {calendarMonths.map(({ year, month }) => (
                      <CalendarMonth
                        key={`${year}-${month}`}
                        year={year}
                        month={month}
                        rangeStart={selectionStart}
                        rangeEnd={selectionEnd}
                        hoverDay={picking === 'start' ? hoverDay : null}
                        onDayClick={handleDayClickSimple}
                        onDayHover={setHoverDay}
                      />
                    ))}
                  </div>

                  {/* Bottom bar */}
                  <div className="border-t border-[#E2E4ED] px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Compare toggle */}
                      <button
                        type="button"
                        onClick={handleCompareToggle}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <div
                          className={`
                            w-8 h-[18px] rounded-full transition-colors relative
                            ${compareEnabled ? 'bg-[#6366F1]' : 'bg-[#E2E4ED]'}
                          `}
                        >
                          <div
                            className={`
                              absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-all
                              ${compareEnabled ? 'left-[15px]' : 'left-[2px]'}
                            `}
                          />
                        </div>
                        <span className="text-sm text-[#1B2559]">Comparer</span>
                      </button>

                      {/* Comparison mode pills */}
                      {compareEnabled && (
                        <div className="flex items-center gap-1">
                          {(['previous_period', 'previous_year', 'custom'] as const).map((mode) => {
                            const labels = {
                              previous_period: 'P\u00e9riode pr\u00e9c\u00e9dente',
                              previous_year: 'Ann\u00e9e pr\u00e9c\u00e9dente',
                              custom: 'Personnalis\u00e9',
                            }
                            return (
                              <button
                                key={mode}
                                type="button"
                                onClick={() => handleCompareModeChange(mode)}
                                className={`
                                  px-2.5 py-1 text-[11px] font-medium rounded-md cursor-pointer transition-colors
                                  ${compareMode === mode
                                    ? 'bg-[#6366F1]/10 text-[#6366F1]'
                                    : 'text-[#8B8FA7] hover:text-[#555A73] hover:bg-[#F4F5FA]'
                                  }
                                `}
                              >
                                {labels[mode]}
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-4 py-1.5 text-sm text-[#8B8FA7] hover:text-[#1B2559] cursor-pointer transition-colors"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={handleApply}
                        disabled={!pendingStart || !pendingEnd}
                        className="bg-[#6366F1] text-white rounded-lg px-4 py-1.5 text-sm font-medium hover:bg-[#5558E6] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                      >
                        Appliquer
                      </button>
                    </div>
                  </div>

                  {/* Custom comparison date inputs (shown below bottom bar) */}
                  {compareEnabled && compareMode === 'custom' && (
                    <div className="border-t border-[#E2E4ED] px-4 py-3 flex items-end gap-3">
                      <div>
                        <label className="block text-[10px] text-[#8B8FA7] uppercase mb-1">D&eacute;but comparaison</label>
                        <input
                          type="text"
                          value={pendingCompareStart}
                          onChange={(e) => setPendingCompareStart(e.target.value)}
                          placeholder="AAAA-MM-JJ"
                          className="border border-[#E2E4ED] rounded px-2 py-1 text-sm w-[130px] text-[#1B2559] outline-none focus:border-[#6366F1]"
                        />
                      </div>
                      <span className="text-[#8B8FA7] text-sm pb-1">&mdash;</span>
                      <div>
                        <label className="block text-[10px] text-[#8B8FA7] uppercase mb-1">Fin comparaison</label>
                        <input
                          type="text"
                          value={pendingCompareEnd}
                          onChange={(e) => setPendingCompareEnd(e.target.value)}
                          placeholder="AAAA-MM-JJ"
                          className="border border-[#E2E4ED] rounded px-2 py-1 text-sm w-[130px] text-[#1B2559] outline-none focus:border-[#6366F1]"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="w-px h-5 bg-[#E2E4ED] mx-1 self-center" />

        {/* Granularity pills */}
        <div className="inline-flex items-center gap-0.5 rounded-lg bg-[#F4F5FA] p-0.5">
          {granularityOptions.map((opt) => (
            <Pill
              key={opt.value}
              active={filters.granularity === opt.value}
              onClick={() => update({ granularity: opt.value })}
            >
              {opt.label}
            </Pill>
          ))}
        </div>

        {/* Divider */}
        <div className="w-px h-5 bg-[#E2E4ED] mx-1 self-center" />

        {/* Device pills */}
        <div className="inline-flex items-center gap-0.5 rounded-lg bg-[#F4F5FA] p-0.5">
          {deviceOptions.map((opt) => (
            <Pill
              key={opt.value}
              active={filters.device === opt.value}
              onClick={() => update({ device: opt.value })}
            >
              {opt.label}
            </Pill>
          ))}
        </div>
      </div>
    </div>
  )
}
